import { z } from 'zod';

import type {
  ForgeHostColumn,
  PersonLinksView,
  PersonLinkView,
} from '@/lib/api/generated/model';
import { linkMethodOf, statusWords } from '@/features/identity';

/**
 * Admin → Linked accounts (spec 160): which workspaces require each person's
 * own account, and who has linked what. Pure: no React, no client.
 */

/** The slices of the people list (spec 162 US2), in the order the chips show. */
export const PEOPLE_FILTERS = [
  'all',
  'missing',
  'broken',
  'expiring',
  'signed-in',
  'token',
] as const;

export type PeopleFilter = (typeof PEOPLE_FILTERS)[number];

/**
 * The people list's view, in the address, like the Limits page's (spec 162
 * R5). The search is coerced from any primitive: the router parses `?q=10` as
 * the number 10, and a string-only schema would drop it silently. An unknown
 * `show`, or a `page` from before this slice, is ignored.
 */
export const peopleSearchSchema = z.object({
  q: z
    .union([z.string(), z.number(), z.boolean()])
    .transform(String)
    .optional()
    .catch(undefined),
  show: z.enum(PEOPLE_FILTERS).optional().catch(undefined),
});

export type PeopleSearch = z.infer<typeof peopleSearchSchema>;

/** The person's link for one host column, or null for "Not linked". */
export function linkCell(
  person: PersonLinksView,
  host: string,
): PersonLinkView | null {
  return person.links.find((link) => link.host === host) ?? null;
}

/**
 * A link's state in the person's own panel's words (spec 159), so an admin
 * and the person never describe the same link differently.
 */
export function cellStatusWords(link: PersonLinkView): string {
  return statusWords(link);
}

/** Dates as the person's panel shows them: the day, no time. */
export function day(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10) : null;
}

/** A token this close to its end is "expiring": the service's reminder window. */
export const EXPIRING_DAYS = 7;

const DAY_MS = 86_400_000;

/** Whole days until a link's expiry, rounded up; `null` without one. */
export function daysLeft(
  link: Pick<PersonLinkView, 'expires_at'>,
  now: Date,
): number | null {
  if (!link.expires_at) return null;
  return Math.ceil((Date.parse(link.expires_at) - now.getTime()) / DAY_MS);
}

export type LinkStanding = 'broken' | 'expiring' | 'fine';

/**
 * One link at a glance (spec 162 R2). Broken covers every link that no longer
 * works — rejected, expired, removed. Only a working personal token can be
 * expiring: a signed-in link renews itself.
 */
export function linkStanding(link: PersonLinkView, now: Date): LinkStanding {
  if (link.status !== 'working') return 'broken';
  if (linkMethodOf(link) === 'token') {
    const days = daysLeft(link, now);
    if (days !== null && days <= EXPIRING_DAYS) return 'expiring';
  }
  return 'fine';
}

/** The hosts some workspace uses: the only ones a person can be missing. */
export function hostsInUse(hosts: readonly ForgeHostColumn[]): string[] {
  return hosts.filter((host) => !host.unused).map((host) => host.host);
}

export type PersonStanding = 'broken' | 'expiring' | 'missing' | 'fine';

const STANDING_ORDER: Record<PersonStanding, number> = {
  broken: 0,
  expiring: 1,
  missing: 2,
  fine: 3,
};

/** A person's links on the hosts in use. */
function linksInUse(row: PersonLinksView, inUse: readonly string[]) {
  return row.links.filter((link) => inUse.includes(link.host));
}

/** Whether a host in use has no link of the person's at all. */
function missesAHost(row: PersonLinksView, inUse: readonly string[]): boolean {
  return inUse.some((host) => !row.links.some((link) => link.host === host));
}

/**
 * A person's worst standing over the hosts in use (spec 162 R2): a broken
 * link, then one about to expire, then a host with no link, then fine. A host
 * no workspace uses never makes anyone look stuck.
 */
export function personStanding(
  row: PersonLinksView,
  inUse: readonly string[],
  now: Date,
): PersonStanding {
  const standings = linksInUse(row, inUse).map((link) =>
    linkStanding(link, now),
  );
  if (standings.includes('broken')) return 'broken';
  if (standings.includes('expiring')) return 'expiring';
  if (missesAHost(row, inUse)) return 'missing';
  return 'fine';
}

function hasWorkingMethod(
  row: PersonLinksView,
  method: 'oauth' | 'token',
): boolean {
  return row.links.some(
    (link) => link.status === 'working' && linkMethodOf(link) === method,
  );
}

/** Whether a person belongs in a slice of the list. */
export function matchesFilter(
  row: PersonLinksView,
  filter: PeopleFilter,
  inUse: readonly string[],
  now: Date,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'missing':
      return missesAHost(row, inUse);
    case 'broken':
      return linksInUse(row, inUse).some(
        (link) => linkStanding(link, now) === 'broken',
      );
    case 'expiring':
      return linksInUse(row, inUse).some(
        (link) => linkStanding(link, now) === 'expiring',
      );
    case 'signed-in':
      return hasWorkingMethod(row, 'oauth');
    case 'token':
      return hasWorkingMethod(row, 'token');
  }
}

/** How many people each slice holds, over everyone, before the search. */
export function filterCounts(
  rows: readonly PersonLinksView[],
  inUse: readonly string[],
  now: Date,
): Record<PeopleFilter, number> {
  const counts = Object.fromEntries(
    PEOPLE_FILTERS.map((filter) => [filter, 0]),
  ) as Record<PeopleFilter, number>;
  for (const row of rows) {
    for (const filter of PEOPLE_FILTERS) {
      if (matchesFilter(row, filter, inUse, now)) counts[filter] += 1;
    }
  }
  return counts;
}

/** A search on name or email, ignoring case; an empty search matches all. */
export function matchesSearch(row: PersonLinksView, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    row.person.display_name.toLowerCase().includes(needle) ||
    row.person.email.toLowerCase().includes(needle)
  );
}

/** Worst first (broken, expiring, missing, fine), then by name. */
export function sortPeople(
  rows: readonly PersonLinksView[],
  inUse: readonly string[],
  now: Date,
): PersonLinksView[] {
  return rows
    .map((row) => ({
      row,
      rank: STANDING_ORDER[personStanding(row, inUse, now)],
    }))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        a.row.person.display_name.localeCompare(b.row.person.display_name),
    )
    .map(({ row }) => row);
}
